import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import NumbersIcon from '@mui/icons-material/Numbers'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'

import {
  buildEntityPatch,
  labelKeyError,
  metricKeyError,
  normalizeMetricKey,
  suggestKnownMetricKey,
  type EntityEditDraft,
  type EntityEditOriginal,
} from './entityEdits'
import { formatMetricValue, type FormatType } from './metrics-config'
import type { EntityDetailResponse, EntityMetricBounds, EntityPatchRequest } from '../../types/gotypes'

/**
 * Correct one post's labels and metrics by hand (#1196).
 *
 * 🔴 WHAT THIS SCREEN IS ACTUALLY FOR, BECAUSE IT CHANGES THE DESIGN. Sky's U35
 * data is gone from production — no `u35` under any spelling, on any entity.
 * This panel and the CSV upload are the only two routes by which it comes back.
 * So the failure to design against is not an awkward form; it is a form that
 * accepts something which LOOKS saved and is not readable afterwards. That is
 * precisely what happened the first time (#1194): a number filed as a label is a
 * string, and every numeric read — the sliders, the metric ranges, cultural
 * relevance — sees nothing at all. It failed silently for months.
 *
 * Three consequences, and they are the whole design:
 *
 *   1. METRICS COME FIRST, above labels. U35 is a metric, it is why this screen
 *      exists, and the section a user reaches first should be the one they came
 *      for.
 *
 *   2. A NUMBER CAN ONLY BE FILED AS A METRIC. The two sections are separate
 *      controls over separate storage, and typing a bare number into a label
 *      says so at the field rather than after a round trip. The server refuses
 *      it too — this is the same rule said earlier, not instead.
 *
 *   3. A METRIC NAME IS NORMALISED, NEVER TAKEN VERBATIM. "U35 Viewers" goes
 *      through the same normaliser the CSV headers go through, so a name typed
 *      here and a spreadsheet column of that name land on ONE key. Two
 *      spellings of U35 is how the original data became unreadable.
 *
 * The panel holds a DRAFT and computes the patch from it; every rule lives in
 * `entityEdits.ts`, which is pure and tested without a render.
 */

export interface EntityEditPanelProps {
  /** What the post says now, from `GET /entities/:id`. Undefined while loading. */
  detail: EntityDetailResponse | undefined
  isLoading?: boolean
  /**
   * The metric names already in use in this job, from the filter summary. They
   * become the select beside the free-text box — reaching for the existing
   * `u35_pct` has to be easier than typing something next to it.
   */
  knownMetrics?: EntityMetricBounds[]
  /** The label keys already in use in this job, same reasoning. */
  knownLabelKeys?: string[]
  onSave: (patch: EntityPatchRequest) => void
  saving?: boolean
  /** The server's own message when a save was refused. It names the key. */
  saveError?: string | null
  /** Shown after a save lands, so a silent success is not indistinguishable from a no-op. */
  savedAt?: number | null
}

/** The draft the panel starts from: every stored value, as text. */
function draftFromDetail(detail: EntityDetailResponse | undefined): EntityEditDraft {
  if (!detail) return { labels: {}, metrics: {} }
  const labels: Record<string, string> = {}
  for (const [key, value] of Object.entries(detail.labels ?? {})) labels[key] = value
  const metrics: Record<string, string> = {}
  for (const [key, reading] of Object.entries(detail.metrics ?? {})) {
    metrics[key] = String(reading.value)
  }
  return { labels, metrics }
}

/** What the draft is diffed against. */
function originalFromDetail(detail: EntityDetailResponse | undefined): EntityEditOriginal {
  if (!detail) return { labels: {}, metrics: {} }
  const metrics: Record<string, number> = {}
  for (const [key, reading] of Object.entries(detail.metrics ?? {})) metrics[key] = reading.value
  return { labels: { ...(detail.labels ?? {}) }, metrics }
}

export const EntityEditPanel: React.FC<EntityEditPanelProps> = ({
  detail,
  isLoading = false,
  knownMetrics = [],
  knownLabelKeys = [],
  onSave,
  saving = false,
  saveError = null,
  savedAt = null,
}) => {
  // ⚠ RESET IN RENDER WHEN THE POST CHANGES, NOT IN AN EFFECT. Selecting another
  // row must not leave the previous post's typing in the boxes — on a screen
  // that overwrites data, that would put one post's numbers over another's with
  // the heading still showing the first. A render-phase guard rather than a
  // useEffect because there is nothing to clean up, and an effect would let one
  // render draw the wrong post's values.
  const entityId = detail?.entity?.id
  const [loadedFor, setLoadedFor] = React.useState<string | undefined>(entityId)
  const [draft, setDraft] = React.useState<EntityEditDraft>(() => draftFromDetail(detail))
  // `savedAt` changes on every successful save, so the boxes also re-seed from
  // what the SERVER stored rather than from what was typed — which is how
  // somebody else's concurrent edit becomes visible instead of invisible.
  const [seededAt, setSeededAt] = React.useState<number | null>(savedAt)
  if (entityId !== loadedFor || savedAt !== seededAt) {
    setLoadedFor(entityId)
    setSeededAt(savedAt)
    setDraft(draftFromDetail(detail))
  }

  const [newMetricName, setNewMetricName] = React.useState('')
  const [newLabelName, setNewLabelName] = React.useState('')

  const original = React.useMemo(() => originalFromDetail(detail), [detail])
  const { patch, errors } = React.useMemo(() => buildEntityPatch(original, draft), [original, draft])

  const setMetric = (key: string, value: string) =>
    setDraft((d) => ({ ...d, metrics: { ...d.metrics, [key]: value } }))
  const setLabel = (key: string, value: string) =>
    setDraft((d) => ({ ...d, labels: { ...d.labels, [key]: value } }))

  // ── the "add a metric" box ────────────────────────────────────────────────
  // The typed name is normalised on every keystroke and the resulting KEY is
  // shown, so the user sees what will be stored before they commit to it.
  const newMetricKey = normalizeMetricKey(newMetricName)
  const newMetricError = (() => {
    if (newMetricName.trim() === '') return null
    const keyError = metricKeyError(newMetricKey)
    if (keyError) return keyError
    if (Object.prototype.hasOwnProperty.call(draft.metrics, newMetricKey)) {
      // Two fields over one value, where whichever was edited last wins
      // silently — the exact ambiguity this screen exists to remove.
      return `This post already has ${newMetricKey} — edit it above`
    }
    return null
  })()
  const canAddMetric = newMetricName.trim() !== '' && newMetricError === null

  // 🔴 THE TRAP THIS SCREEN COULD OTHERWISE WALK STRAIGHT INTO, SAID OUT LOUD.
  // The CSV path normalises a header AND THEN resolves it through the catalog's
  // legacy aliases, where `u35_percent` and `u35_viewers` both land on the
  // canonical `u35_pct`. The PATCH endpoint does only the first half: it checks
  // the key's shape and writes it verbatim. So "U35%" typed here saves cleanly
  // as `u35_percent` — a key no chart asks for — and the number is filed,
  // invisible, and looks filed. That is #1194's exact shape arriving through the
  // door built to prevent it.
  //
  // A WARNING RATHER THAN A REWRITE, and rather than a refusal. The browser has
  // no metric catalog by design (EN-T20 deleted it; the API is the only carrier
  // of metric metadata), so rewriting would mean a hard-coded alias table here —
  // the second source of truth that rule exists to forbid. And Sky genuinely
  // does add metrics of their own, so this must not be the screen that refuses
  // them.
  const knownMetricKeys = React.useMemo(() => knownMetrics.map((m) => m.metric), [knownMetrics])
  const newMetricNotice = (() => {
    if (newMetricName.trim() === '' || newMetricError !== null) return null
    if (knownMetricKeys.includes(newMetricKey)) return null
    const suggestion = suggestKnownMetricKey(newMetricKey, knownMetricKeys)
    const opening = `${newMetricKey} is a new metric — nothing on the dashboards reads it yet.`
    return suggestion ? `${opening} Did you mean ${suggestion}?` : opening
  })()

  const addMetric = () => {
    if (!canAddMetric) return
    setDraft((d) => ({ ...d, metrics: { ...d.metrics, [newMetricKey]: '' } }))
    setNewMetricName('')
  }

  // ── the "add a label" box ─────────────────────────────────────────────────
  // 🔴 A LABEL NAME IS NOT NORMALISED, DELIBERATELY. Label keys are free-form
  // across the whole product — channel manifests, importers and ThemeAI all
  // mint them — so normalising here would rename keys the rest of the system
  // writes as they are. Only the always-wrong shapes are refused, matching the
  // server.
  const newLabelKey = newLabelName
  const newLabelError = (() => {
    if (newLabelName === '') return null
    const keyError = labelKeyError(newLabelKey)
    if (keyError) return keyError
    if (Object.prototype.hasOwnProperty.call(draft.labels, newLabelKey)) {
      return `This post already has ${newLabelKey} — edit it above`
    }
    return null
  })()
  const canAddLabel = newLabelName.trim() !== '' && newLabelError === null

  const addLabel = () => {
    if (!canAddLabel) return
    setDraft((d) => ({ ...d, labels: { ...d.labels, [newLabelKey]: '' } }))
    setNewLabelName('')
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }
  if (!detail) return null

  const metricKeys = Object.keys(draft.metrics).sort()
  const labelKeys = Object.keys(draft.labels).sort()
  const readingFor = (key: string) => detail.metrics?.[key]

  return (
    <Box data-testid="entity-edit-panel" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── METRICS, FIRST ────────────────────────────────────────────────
          Above labels because U35 is a metric and is why this screen exists.
          The section a user reaches first should be the one they came for. */}
      <Box>
        <SectionTitle icon={<NumbersIcon fontSize="small" />} title="Metrics" caption="Numbers. These are what the charts read." />

        {metricKeys.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="entity-metrics-empty" sx={{ py: 1 }}>
            This post carries no metrics yet. Add one below.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {metricKeys.map((key) => {
              const reading = readingFor(key)
              const error = errors[`metric:${key}`]
              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {reading?.displayName || key}
                    </Typography>
                    {/* The KEY, always, beside the display name. It is what a
                        CSV column and a filter have to match, and a screen that
                        showed only "U35 %" is how somebody comes to believe the
                        name is the thing. */}
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {key}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      size="small"
                      value={draft.metrics[key]}
                      onChange={(e) => setMetric(key, e.target.value)}
                      error={Boolean(error)}
                      slotProps={{ htmlInput: { 'data-testid': `entity-metric-input-${key}` } }}
                      sx={{ width: 180 }}
                    />
                    <FieldError testId={`entity-metric-error-${key}`} message={error} />
                  </Box>
                  {reading && (
                    <Box sx={{ pt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">
                        now {formatMetricValue(reading.value, (reading.format as FormatType) || 'number')}
                      </Typography>
                      {/* Provenance, because the next action is an overwrite:
                          somebody replacing a number should be able to see
                          whether Sprinklr supplied it or a person did. */}
                      {reading.source && (
                        <Chip
                          label={reading.source}
                          size="small"
                          variant="outlined"
                          data-testid={`entity-metric-source-${key}`}
                          sx={{ height: 18, fontSize: '0.65rem', ml: 1 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        )}

        <AddRow
          testIdPrefix="entity-add-metric"
          label="Add a metric"
          knownLabel="Names already in use"
          known={knownMetrics.map((m) => ({ value: m.metric, label: m.displayName || m.metric }))}
          name={newMetricName}
          onNameChange={setNewMetricName}
          /* The resulting key is shown live, so "U35 Viewers" visibly becomes
             u35_viewers BEFORE it is committed. */
          resolvedKey={newMetricName.trim() === '' ? '' : newMetricKey}
          error={newMetricError}
          notice={newMetricNotice}
          canAdd={canAddMetric}
          onAdd={addMetric}
          hint="Typed names are tidied into a key the same way a CSV column header is, so a name typed here matches the same column in a spreadsheet."
        />
      </Box>

      <Divider />

      {/* ── LABELS ───────────────────────────────────────────────────────── */}
      <Box>
        <SectionTitle
          icon={<LabelOutlinedIcon fontSize="small" />}
          title="Labels"
          caption="Text. A number belongs in a metric, not here."
        />

        {labelKeys.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="entity-labels-empty" sx={{ py: 1 }}>
            This post carries no labels yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {labelKeys.map((key) => {
              const error = errors[`label:${key}`]
              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: 200, pt: 1, fontFamily: 'monospace', fontWeight: 600 }}
                  >
                    {key}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      size="small"
                      value={draft.labels[key]}
                      onChange={(e) => setLabel(key, e.target.value)}
                      error={Boolean(error)}
                      slotProps={{ htmlInput: { 'data-testid': `entity-label-input-${key}` } }}
                      sx={{ width: 320 }}
                    />
                    <FieldError testId={`entity-label-error-${key}`} message={error} />
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}

        <AddRow
          testIdPrefix="entity-add-label"
          label="Add a label"
          knownLabel="Names already in use"
          known={knownLabelKeys.map((k) => ({ value: k, label: k }))}
          name={newLabelName}
          onNameChange={setNewLabelName}
          resolvedKey=""
          error={newLabelError}
          canAdd={canAddLabel}
          onAdd={addLabel}
          hint="Label names are used as typed — the rest of the product mints them freely, so they are not rewritten here."
        />
      </Box>

      {saveError && (
        <Alert severity="error" data-testid="entity-edit-error">
          {saveError}
        </Alert>
      )}

      {/* Pinned to the bottom of whatever scrolls the panel (the edit dialog),
          so Save is always in view however many metrics the post carries. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'sticky',
          // Past the dialog's 20px bottom padding, so scrolled content cannot
          // show through beneath the bar; the extra pb puts the space back.
          bottom: -20,
          zIndex: 1,
          pt: 1.5,
          pb: '32px',
          mb: '-20px',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          color="info"
          variant="contained"
          startIcon={<SaveIcon />}
          data-testid="entity-edit-save"
          /* Disabled with nothing to save, and disabled while ANY field is in
             error. The endpoint validates before it writes, so a body with one
             bad key writes nothing — offering a click that saves half of what is
             on screen would be worse than offering none. */
          disabled={saving || patch === null}
          onClick={() => patch && onSave(patch)}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        {/* Only CHANGED fields travel. Saying so out loud is what makes a merge
            trustworthy on a screen two people might have open at once. */}
        <Typography variant="caption" color="text.secondary" data-testid="entity-edit-summary">
          {patch === null
            ? Object.keys(errors).length > 0
              ? 'Fix the fields above to save'
              : 'No changes yet'
            : `Saving ${changeCount(patch)} change${changeCount(patch) === 1 ? '' : 's'} — untouched fields are left alone`}
        </Typography>
      </Box>
    </Box>
  )
}

/**
 * The message under one field.
 *
 * Its own element rather than MUI's `helperText` slot: the slot's typed
 * `slotProps` will not carry a `data-testid`, and every refusal in this panel is
 * worth asserting by test — a rule nothing checks is a rule that quietly stops
 * being enforced, which is how #1194 ran for months.
 */
const FieldError: React.FC<{ testId: string; message?: string | null }> = ({ testId, message }) =>
  message ? (
    <Typography variant="caption" color="error" data-testid={testId} sx={{ mt: 0.5, maxWidth: 320 }}>
      {message}
    </Typography>
  ) : null

function changeCount(patch: EntityPatchRequest): number {
  return Object.keys(patch.labels ?? {}).length + Object.keys(patch.metrics ?? {}).length
}

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; caption: string }> = ({
  icon,
  title,
  caption,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
      {icon}
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
      >
        {title}
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">
      {caption}
    </Typography>
  </Box>
)

interface AddRowProps {
  testIdPrefix: string
  label: string
  knownLabel: string
  known: Array<{ value: string; label: string }>
  name: string
  onNameChange: (value: string) => void
  /** The key the typed name resolves to, shown live. Empty hides the chip. */
  resolvedKey: string
  error: string | null
  /**
   * A non-blocking warning shown under the box — today, "this key is new and
   * nothing reads it". Separate from `error` because it must NOT disable the
   * Add button: it is information, not a refusal.
   */
  notice?: string | null
  canAdd: boolean
  onAdd: () => void
  hint: string
}

/**
 * "A text box PLUS a select of names already in use."
 *
 * 🔴 THE SELECT IS THE CHEAP HALF OF PREVENTING #1194. Free text alone is how
 * "U35 %" gets minted beside `u35_pct`; a list of what already exists makes
 * reaching for the existing name the shorter path. The normaliser is the other
 * half, for when somebody types anyway.
 *
 * Picking from the select writes the KEY into the text box rather than adding
 * straight away, so there is one place the name being added is shown and one
 * button that commits it — two paths that both add would be two places to get
 * the confirmation wrong.
 */
const AddRow: React.FC<AddRowProps> = ({
  testIdPrefix,
  label,
  knownLabel,
  known,
  name,
  onNameChange,
  resolvedKey,
  error,
  notice = null,
  canAdd,
  onAdd,
  hint,
}) => (
  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TextField
          size="small"
          label={label}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          error={Boolean(error)}
          slotProps={{ htmlInput: { 'data-testid': `${testIdPrefix}-name` } }}
          sx={{ width: 260 }}
        />
        <FieldError testId={`${testIdPrefix}-error`} message={error} />
      </Box>

      {known.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 220 }} data-testid={`${testIdPrefix}-known`}>
          <InputLabel>{knownLabel}</InputLabel>
          <Select
            label={knownLabel}
            value=""
            onChange={(e) => onNameChange(String(e.target.value))}
          >
            {known.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                data-testid={`${testIdPrefix}-known-option-${option.value}`}
              >
                {option.label}
                {option.label !== option.value && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontFamily: 'monospace' }}>
                    {option.value}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button
        color="info"
        variant="outlined"
        size="medium"
        startIcon={<AddIcon />}
        disabled={!canAdd}
        onClick={onAdd}
        data-testid={`${testIdPrefix}-confirm`}
        sx={{ mt: 0.25 }}
      >
        Add
      </Button>
    </Box>

    {notice && (
      <Alert severity="warning" data-testid={`${testIdPrefix}-notice`} sx={{ py: 0, mt: 0.5 }}>
        <Typography variant="caption">{notice}</Typography>
      </Alert>
    )}

    {resolvedKey !== '' && (
      <Typography variant="caption" color="text.secondary" data-testid={`${testIdPrefix}-key`}>
        will be stored as <code>{resolvedKey}</code>
      </Typography>
    )}
    <Typography variant="caption" color="text.secondary">
      {hint}
    </Typography>
  </Box>
)

export default EntityEditPanel
